import { useState, useEffect, useRef } from 'preact/hooks';
import { UnifiedComposer } from './UnifiedComposer';
import { chorusCount, takesSoFar } from '../copy/counts';
import type { PromptFlowState, SubmitResult, UnifiedSubmitEntry } from '../pages/ContributePage';
import type { Contribution, LiveDigest, StoryContext } from '../api/types';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import styles from './ContributeSheet.module.css';

interface ContributeSheetProps {
  open: boolean;
  onClose: () => void;
  onUnifiedSubmit: (entries: UnifiedSubmitEntry[]) => Promise<SubmitResult>;
  submitting: boolean;
  threadId: string;
  prompts?: string[];
  promptExamples?: string[];
  promptStates?: Map<number, PromptFlowState>;
  draftsPostable?: boolean;
  closeBlocked?: boolean;
  allowedTypes?: Array<'text' | 'photo' | 'audio' | 'video'>;
  buildOnTarget?: Contribution | null;
  onClearBuildOn?: () => void;
  threadType?: string;
  storyContext?: StoryContext | null;
  liveDigest?: LiveDigest | null;
  phaseName?: string;
  phaseDescription?: string;
  /** Title of the moment the take joins — anchors the success screen. */
  momentTitle?: string | null;
  /** Navigates to the thread from the success screen's chorus CTA. */
  onViewChorus?: () => void;
  /** Route to the finished recap, used once the story is over. */
  onViewPayoff?: () => void;
}

type SheetLayout = 'sheet' | 'modal' | 'split';

export function ContributeSheet({
  open,
  onClose,
  onUnifiedSubmit,
  submitting,
  prompts,
  promptExamples,
  promptStates,
  draftsPostable = true,
  closeBlocked = false,
  allowedTypes,
  buildOnTarget,
  onClearBuildOn,
  threadId,
  threadType,
  storyContext,
  liveDigest,
  phaseName,
  phaseDescription,
  momentTitle,
  onViewChorus,
  onViewPayoff,
}: ContributeSheetProps) {
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [error, setError] = useState<string | null>(null);
  const [layout, setLayout] = useState<SheetLayout>(() => getLayout(typeof window === 'undefined' ? 0 : window.innerWidth));
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [isContextExpandedWhileFocused, setIsContextExpandedWhileFocused] = useState(false);
  const [isFullCapture, setIsFullCapture] = useState(false);
  const [composerResetKey, setComposerResetKey] = useState(0);
  const [hasUnsentWork, setHasUnsentWork] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const onResize = () => setLayout(getLayout(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Focus management: capture the trigger element when opening so we can
  // restore focus on close, and move focus into the dialog so screen-reader
  // and keyboard users land inside the modal instead of remaining on the FAB.
  useEffect(() => {
    if (open) {
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (active instanceof HTMLElement) {
        previouslyFocusedRef.current = active;
      }
      // Defer to after the open transition / render so the ref is mounted.
      const raf = typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame(() => sheetRef.current?.focus())
        : (setTimeout(() => sheetRef.current?.focus(), 0) as unknown as number);
      return () => {
        if (typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(raf);
        else clearTimeout(raf);
      };
    }
    const timeoutId = setTimeout(() => {
      setVisibility('public');
      setError(null);
      setIsComposerFocused(false);
      setIsContextExpandedWhileFocused(false);
      setIsFullCapture(false);
      setHasUnsentWork(false);
      setConfirmDiscard(false);
      setComposerResetKey((current) => current + 1);
    }, 300);
    // Restore focus to whatever opened the sheet.
    const trigger = previouslyFocusedRef.current;
    previouslyFocusedRef.current = null;
    if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
      trigger.focus();
    }
    return () => clearTimeout(timeoutId);
  }, [open]);

  const requestClose = (control = 'done_for_now') => {
    if (closeBlocked) return;
    trackCaptureEvent('prompt_flow_exited', {
      surface: 'prompt-flow',
      threadId,
      answered: [...(promptStates?.values() ?? [])].filter((state) => state === 'answered').length,
      queued: [...(promptStates?.values() ?? [])].filter((state) => state === 'queued').length,
      hasUnsentDraft: hasUnsentWork,
      control,
    });
    if (hasUnsentWork) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const handleOverlayClick = (event: MouseEvent) => {
    if (event.target === overlayRef.current) {
      requestClose('overlay');
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (closeBlocked) return;
      event.preventDefault();
      event.stopPropagation();
      requestClose('escape');
      return;
    }
    if (event.key !== 'Tab' || !sheetRef.current) return;
    // Filter on RENDERED visibility, not just the `hidden` attribute: the composer's file
    // inputs are hidden with `display: none` and sit last in the DOM, so an attribute-only
    // filter leaves `last` permanently unfocusable and Tab escapes the dialog.
    const focusable = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    )).filter((element) => {
      if (element.hasAttribute('hidden')) return false;
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleUnifiedSubmit = async (entries: UnifiedSubmitEntry[]) => {
    setError(null);
    try {
      return await onUnifiedSubmit(entries.map((entry) => ({ ...entry, visibility })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
      return {
        delivery: 'failed',
        createdIndices: [],
        queuedIndices: [],
        failedIndices: entries.flatMap((entry) => entry.promptMeta ? [entry.promptMeta.promptIndex] : []),
        createdCount: 0,
        next: null,
      } satisfies SubmitResult;
    }
  };

  const buildOnAuthor = buildOnTarget?.author?.displayName ?? 'someone';
  const buildOnPreview = buildOnTarget?.content
    ? buildOnTarget.content.length > 100
      ? `${buildOnTarget.content.slice(0, 100)}...`
      : buildOnTarget.content
    : null;

  const hasMissionCard = Boolean(
    phaseDescription ||
    storyContext?.backstory ||
    storyContext?.setting ||
    storyContext?.contributorGuidance,
  );
  const hasStoryCard = Boolean(liveDigest?.narrativeSummary);
  const hasCommunityCard = Boolean(
    (liveDigest?.contributionCount ?? 0) > 0 ||
    (liveDigest?.recentVoices.length ?? 0) > 0 ||
    (liveDigest?.emergingThemes.length ?? 0) > 0,
  );
  const hasContext = !buildOnTarget && (hasMissionCard || hasStoryCard || hasCommunityCard);
  const showCollapsedContext = hasContext && isComposerFocused && !isContextExpandedWhileFocused;
  const themeList = liveDigest?.semanticThemes?.length ? liveDigest.semanticThemes : liveDigest?.emergingThemes ?? [];
  const contributionSummary = (liveDigest?.contributionCount ?? 0) > 0
    ? takesSoFar(liveDigest?.contributionCount ?? 0)
    : 'Tap to expand';

  return (
    <div
      ref={overlayRef}
      class={`${styles.overlay} ${open ? styles.overlayOpen : ''}`}
      onClick={handleOverlayClick}
    >
      <div
        ref={sheetRef}
        class={`${styles.sheet} ${open ? styles.sheetOpen : ''} ${isFullCapture && layout === 'sheet' ? styles.sheetFullCapture : ''}`}
        data-layout={layout}
        role="dialog"
        aria-modal="true"
        aria-label={buildOnTarget ? 'Build on contribution' : 'Add contribution'}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {!(isFullCapture && layout === 'sheet') && <div class={styles.handle} />}

        {!(isFullCapture && layout === 'sheet') && (
          <div class={styles.sheetHeader}>
            <h3 class={styles.sheetTitle}>{buildOnTarget ? 'Build On' : 'Add Your Take'}</h3>
            <button type="button" class={styles.closeButton} onClick={() => requestClose('close_button')} aria-label="Close composer">&times;</button>
          </div>
        )}

        {buildOnTarget && (
          <div class={styles.buildOnBanner}>
            <div class={styles.buildOnBannerHeader}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="15 10 20 15 15 20" />
                <path d="M4 4v7a4 4 0 004 4h12" />
              </svg>
              <span>Building on {buildOnAuthor}&rsquo;s take</span>
              {onClearBuildOn && (
                <button type="button" class={styles.buildOnDismiss} onClick={onClearBuildOn} aria-label="Clear build-on target">&times;</button>
              )}
            </div>
            {buildOnPreview && <p class={styles.buildOnPreview}>&ldquo;{buildOnPreview}&rdquo;</p>}
          </div>
        )}

        <div class={`${styles.sheetBody} ${isFullCapture && layout === 'sheet' ? styles.sheetBodyFullCapture : ''}`}>
          {hasContext && !(isFullCapture && layout === 'sheet') && (
            <div class={styles.contextColumn}>
              {showCollapsedContext ? (
                <button
                  type="button"
                  class={styles.compactContextBar}
                  onClick={() => setIsContextExpandedWhileFocused(true)}
                >
                  <div class={styles.compactContextMeta}>
                    <span class={styles.compactContextTitle}>{phaseName ?? 'Story Context'}</span>
                    <span class={styles.compactContextSubtitle}>
                      {contributionSummary}
                    </span>
                  </div>
                  <span class={styles.compactContextChevron}>&#9660;</span>
                </button>
              ) : (
                <div class={`${styles.contextDeck} ${layout === 'split' ? styles.contextDeckSplit : ''}`}>
                  {isComposerFocused && (
                    <div class={styles.contextDeckHeader}>
                      <span class={styles.contextDeckLabel}>Story Context</span>
                      <button
                        type="button"
                        class={styles.contextCollapseButton}
                        onClick={() => setIsContextExpandedWhileFocused(false)}
                      >
                        Collapse
                      </button>
                    </div>
                  )}

                  {hasMissionCard && (
                    <section class={styles.contextCard}>
                      <h4 class={styles.contextCardTitle}>The Mission</h4>
                      {phaseDescription && <p class={styles.contextCardText}>{phaseDescription}</p>}
                      {!phaseDescription && storyContext?.backstory && (
                        <p class={styles.contextCardText}>{storyContext.backstory}</p>
                      )}
                      {phaseDescription && storyContext?.backstory && (
                        <p class={styles.contextCardSubtle}>{storyContext.backstory}</p>
                      )}
                      {storyContext?.setting && <p class={styles.contextMeta}>Setting: {storyContext.setting}</p>}
                      {storyContext?.contributorGuidance && (
                        <p class={styles.contextGuidance}>{storyContext.contributorGuidance}</p>
                      )}
                    </section>
                  )}

                  {hasStoryCard && (
                    <section class={styles.contextCard}>
                      <h4 class={styles.contextCardTitle}>Story So Far</h4>
                      <p class={styles.contextCardText}>{liveDigest?.narrativeSummary}</p>
                    </section>
                  )}

                  {hasCommunityCard && (
                    <section class={styles.contextCard}>
                      <h4 class={styles.contextCardTitle}>Community Pulse</h4>
                      <p class={styles.contextMeta}>
                        {chorusCount(liveDigest?.contributionCount ?? 0, liveDigest?.contributorCount ?? 0)}
                      </p>

                      {themeList.length > 0 && (
                        <div class={styles.themeList}>
                          {themeList.map((theme) => (
                            <span key={theme} class={styles.themeTag}>{theme}</span>
                          ))}
                        </div>
                      )}

                      {liveDigest?.dominantVoice && (
                        <p class={styles.contextCardSubtle}>
                          Dominant voice: {liveDigest.dominantVoice.displayName} ({Math.round(liveDigest.dominantVoice.share * 100)}%)
                        </p>
                      )}

                      {liveDigest?.standoutMoment && (
                        <p class={styles.contextCardSubtle}>
                          Standout moment: {liveDigest.standoutMoment.displayName} - {liveDigest.standoutMoment.snippet}
                        </p>
                      )}

                      {(liveDigest?.recentVoices.length ?? 0) > 0 && (
                        <div class={styles.voiceList}>
                          {liveDigest?.recentVoices.slice(0, 3).map((voice) => (
                            <div key={`${voice.displayName}-${voice.contributionType}`} class={styles.voiceRow}>
                              <span class={styles.voiceAvatar}>{voice.displayName.charAt(0).toUpperCase()}</span>
                              <div class={styles.voiceMeta}>
                                <span class={styles.voiceName}>{voice.displayName}</span>
                                <span class={styles.voiceSnippet}>{voice.snippet}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  )}
                </div>
              )}
            </div>
          )}

          <div class={styles.formArea}>
            <UnifiedComposer
            key={composerResetKey}
              onSubmit={handleUnifiedSubmit}
              submitting={submitting}
              initialContent=""
              threadId={threadId}
              threadType={threadType}
              prompts={prompts}
              promptExamples={promptExamples}
              promptStates={promptStates}
              allowedTypes={allowedTypes}
              visibility={visibility}
              onVisibilityChange={setVisibility}
              onFocusChange={(focused) => {
                setIsComposerFocused(focused);
                if (!focused) {
                  setIsContextExpandedWhileFocused(false);
                }
              }}
              onRequestFullCapture={setIsFullCapture}
              onDirtyChange={setHasUnsentWork}
              onRequestClose={requestClose}
              momentTitle={momentTitle}
              contributionCount={liveDigest?.contributionCount}
              contributorCount={liveDigest?.contributorCount}
              contributorNames={liveDigest?.recentVoices?.map((v) => v.displayName)}
              onViewChorus={onViewChorus}
              onViewPayoff={onViewPayoff}
            />
          </div>
        </div>

        {error && <p class={styles.error}>{error}</p>}
        {confirmDiscard && (
          <div class={styles.discardGuard} role="alertdialog" aria-modal="true" aria-labelledby="discard-draft-title">
            <h4 id="discard-draft-title">
              {draftsPostable ? 'Discard your unshared draft?' : 'Your unshared drafts can’t be posted in this phase'}
            </h4>
            <p>
              {draftsPostable
                ? 'Your draft will be removed from this composer.'
                : 'You’ve shared all your takes here.'}
            </p>
            <div class={styles.discardActions}>
              {draftsPostable && (
                <button type="button" class={styles.keepWritingButton} onClick={() => setConfirmDiscard(false)}>
                  Keep writing
                </button>
              )}
              <button
                type="button"
                class={draftsPostable ? styles.discardButton : styles.keepWritingButton}
                onClick={() => {
                  setConfirmDiscard(false);
                  setHasUnsentWork(false);
                  onClose();
                }}
              >
                {draftsPostable ? 'Discard draft' : 'Done'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function getLayout(width: number): SheetLayout {
  if (width >= 1200) return 'split';
  if (width >= 960) return 'modal';
  return 'sheet';
}
